import { useState, useEffect } from "react";
import { History, RefreshCw, Search, Download, Calendar, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useOperationLogs, OperationLog } from "@/hooks/useOperationLogs";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";
import { toast } from "sonner";

const actionColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  create: "default",
  update: "secondary",
  delete: "destructive",
  open: "default",
  close: "secondary",
  transfer: "outline",
  cancel: "destructive",
  complete: "default",
};

export default function LogsPage() {
  const {
    logs,
    totalLogs,
    isLoading,
    fetchLogs,
    getActionLabel,
    getEntityLabel,
  } = useOperationLogs();

  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);
  const pageSize = 20;
  const totalPages = Math.ceil(totalLogs / pageSize);

  useEffect(() => {
    fetchLogs({
      page,
      pageSize,
      action: actionFilter !== "all" ? actionFilter : undefined,
      entityType: entityFilter !== "all" ? entityFilter : undefined,
      dateFrom,
      dateTo,
    });
  }, [page, actionFilter, entityFilter, dateFrom, dateTo, fetchLogs]);

  const handleClearFilters = () => {
    setActionFilter("all");
    setEntityFilter("all");
    setSearchText("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setPage(1);
  };

  const hasFilters = actionFilter !== "all" || entityFilter !== "all" || searchText || dateFrom || dateTo;

  // Filter logs by search text in details
  const filteredLogs = searchText
    ? logs.filter((log) => {
        const detailsStr = log.details ? JSON.stringify(log.details).toLowerCase() : "";
        const userName = log.profile?.full_name?.toLowerCase() || "";
        const searchLower = searchText.toLowerCase();
        return detailsStr.includes(searchLower) || userName.includes(searchLower);
      })
    : logs;

  const formatDetails = (details: Record<string, any> | null): string => {
    if (!details) return "-";
    
    const parts: string[] = [];
    
    if (details.name) parts.push(`Nome: ${details.name}`);
    if (details.number) parts.push(`Número: ${details.number}`);
    if (details.order_number) parts.push(`Pedido #${details.order_number}`);
    if (details.type) parts.push(`Tipo: ${details.type}`);
    if (details.amount !== undefined) parts.push(`Valor: R$ ${Number(details.amount).toFixed(2)}`);
    if (details.price !== undefined) parts.push(`Preço: R$ ${Number(details.price).toFixed(2)}`);
    if (details.quantity !== undefined) parts.push(`Qtd: ${details.quantity}`);
    if (details.reason) parts.push(`Motivo: ${details.reason}`);
    
    if (details.changes) {
      Object.entries(details.changes).forEach(([key, value]: [string, any]) => {
        if (value && typeof value === 'object' && 'old' in value && 'new' in value) {
          parts.push(`${key}: ${value.old} → ${value.new}`);
        }
      });
    }
    
    if (details.status_change) {
      parts.push(`Status: ${details.status_change.old} → ${details.status_change.new}`);
    }
    
    if (details.opening_amount !== undefined) {
      parts.push(`Abertura: R$ ${Number(details.opening_amount).toFixed(2)}`);
    }
    if (details.closing_amount !== undefined) {
      parts.push(`Fechamento: R$ ${Number(details.closing_amount).toFixed(2)}`);
    }
    if (details.difference !== undefined) {
      parts.push(`Diferença: R$ ${Number(details.difference).toFixed(2)}`);
    }
    
    if (details.batch) {
      parts.push(`Lote: ${details.startNumber} a ${details.endNumber} (${details.count} itens)`);
    }
    
    if (parts.length === 0) {
      return JSON.stringify(details);
    }
    
    return parts.join(" | ");
  };

  const exportToExcel = () => {
    if (filteredLogs.length === 0) {
      toast.error("Nenhum log para exportar");
      return;
    }

    const exportData = filteredLogs.map((log) => ({
      "Data/Hora": format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR }),
      "Usuário": log.profile?.full_name || "Usuário",
      "Ação": getActionLabel(log.action),
      "Entidade": getEntityLabel(log.entity_type),
      "Detalhes": formatDetails(log.details),
      "ID Entidade": log.entity_id || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Logs");

    // Auto-size columns
    const colWidths = [
      { wch: 20 }, // Data/Hora
      { wch: 25 }, // Usuário
      { wch: 15 }, // Ação
      { wch: 20 }, // Entidade
      { wch: 60 }, // Detalhes
      { wch: 40 }, // ID Entidade
    ];
    ws["!cols"] = colWidths;

    const fileName = `logs_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Logs exportados com sucesso!");
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <History className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Logs de Operações</h1>
            <p className="text-sm text-muted-foreground">
              {totalLogs} registro(s) encontrado(s)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportToExcel}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchLogs({ page, pageSize })}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        {/* Date From */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("w-[140px] justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}>
              <Calendar className="h-4 w-4 mr-2" />
              {dateFrom ? format(dateFrom, "dd/MM/yy") : "Data início"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={dateFrom}
              onSelect={(date) => { setDateFrom(date); setPage(1); }}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>

        {/* Date To */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("w-[140px] justify-start text-left font-normal", !dateTo && "text-muted-foreground")}>
              <Calendar className="h-4 w-4 mr-2" />
              {dateTo ? format(dateTo, "dd/MM/yy") : "Data fim"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={dateTo}
              onSelect={(date) => { setDateTo(date); setPage(1); }}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>

        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Ação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Ações</SelectItem>
            <SelectItem value="create">Criar</SelectItem>
            <SelectItem value="update">Atualizar</SelectItem>
            <SelectItem value="delete">Excluir</SelectItem>
            <SelectItem value="open">Abrir</SelectItem>
            <SelectItem value="close">Fechar</SelectItem>
            <SelectItem value="transfer">Transferir</SelectItem>
            <SelectItem value="cancel">Cancelar</SelectItem>
          </SelectContent>
        </Select>

        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Entidade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Entidades</SelectItem>
            <SelectItem value="order">Pedido</SelectItem>
            <SelectItem value="product">Produto</SelectItem>
            <SelectItem value="category">Categoria</SelectItem>
            <SelectItem value="table">Mesa</SelectItem>
            <SelectItem value="command">Comanda</SelectItem>
            <SelectItem value="cash_register">Caixa</SelectItem>
            <SelectItem value="cash_movement">Movimentação</SelectItem>
            <SelectItem value="payment_method">Forma de Pagamento</SelectItem>
            <SelectItem value="ingredient">Ingrediente</SelectItem>
            <SelectItem value="stock_movement">Mov. Estoque</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar nos detalhes..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 w-[200px]"
          />
        </div>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-12">
          <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhum log encontrado</h3>
          <p className="text-muted-foreground">
            Os registros de operações aparecerão aqui
          </p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Data/Hora</TableHead>
                  <TableHead className="w-[150px]">Usuário</TableHead>
                  <TableHead className="w-[100px]">Ação</TableHead>
                  <TableHead className="w-[130px]">Entidade</TableHead>
                  <TableHead>Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedLog(log)}
                  >
                    <TableCell className="whitespace-nowrap font-mono text-sm">
                      {format(new Date(log.created_at), "dd/MM/yy HH:mm", {
                        locale: ptBR,
                      })}
                    </TableCell>
                    <TableCell className="truncate max-w-[150px]">
                      {log.profile?.full_name || "Usuário"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={actionColors[log.action] || "secondary"}>
                        {getActionLabel(log.action)}
                      </Badge>
                    </TableCell>
                    <TableCell>{getEntityLabel(log.entity_type)}</TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                      {formatDetails(log.details)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {/* Log Details Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge variant={actionColors[selectedLog?.action || ""] || "secondary"}>
                {selectedLog ? getActionLabel(selectedLog.action) : ""}
              </Badge>
              <span>{selectedLog ? getEntityLabel(selectedLog.entity_type) : ""}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Data/Hora</p>
                  <p className="font-medium">
                    {format(new Date(selectedLog.created_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Usuário</p>
                  <p className="font-medium">{selectedLog.profile?.full_name || "Usuário"}</p>
                </div>
                {selectedLog.entity_id && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">ID da Entidade</p>
                    <p className="font-mono text-xs">{selectedLog.entity_id}</p>
                  </div>
                )}
              </div>
              
              {selectedLog.details && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Detalhes</p>
                  <div className="bg-muted p-3 rounded-lg">
                    <pre className="text-xs whitespace-pre-wrap overflow-auto max-h-[300px]">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
